import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Fragment, useId, useState, useEffect, useRef, useCallback } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import moment from 'moment';
import { noop, sortBy } from 'lodash';
import { cleanupId } from 'Utils/unique-id/uniqueId';
import type { ValidationState } from 'Store/trip-information/kids/constants';
import 'Store/trip-information/kids/constants';
import { FormControl } from 'Components/frameworks/form-inputs/form-control/FormControl';
import { themes } from './InputDate.themes';
const DAY = 'd';
const MONTH = 'm';
const YEAR = 'y';
const DAY_LENGTH = 2;
const MONTH_LENGTH = 2;
const YEAR_LENGTH = 4;
type Props = {
  className?: string;
  dateFormat?: string;
  hasClear?: boolean;
  iconLeft?: IconName;
  id?: string;
  isDisabled?: boolean;
  isFilled?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label?: string | ReactNode;
  name?: string;
  notes?: string;
  onBlur?: (arg0: React.SyntheticEvent) => void;
  onChange?: (name: string, value: timestamp | boolean | null) => Promise<void>;
  onClear?: (arg0: React.SyntheticEvent) => void;
  onClick?: (arg0: React.SyntheticEvent) => void;
  onFocus?: (arg0: React.SyntheticEvent) => void;
  onKeyDown?: (arg0: React.KeyboardEvent) => void;
  onKeyUp?: (arg0: React.KeyboardEvent) => void;
  placeholderDay?: string;
  placeholderMonth?: string;
  placeholderYear?: string;
  theme?: ThemeKey<typeof themes>;
  validationState?: ValidationState;
  value?: timestamp | null;
};

function getInputsState(value: Props['value']) {
  const date = value ? moment(value) : moment(null);
  return {
    dayValue: date.isValid() ? date.format('DD') : '',
    monthValue: date.isValid() ? date.format('MM') : '',
    yearValue: date.isValid() ? date.format('YYYY') : '',
  };
}

const defaultProps = {
  dateFormat: 'DDMMYYYY',
  onChange: () => Promise.resolve(),
  theme: 'default' as const,
};

export function InputDate(props: Props) {
  const {
    className,
    dateFormat = defaultProps.dateFormat,
    hasClear,
    iconLeft,
    id: idProp,
    isDisabled,
    isFilled,
    isLoading,
    isReadOnly,
    isRequired,
    label,
    name,
    notes,
    onBlur,
    onChange = defaultProps.onChange,
    onClear: onClearProp = noop,
    onClick,
    onFocus,
    onKeyDown,
    onKeyUp,
    theme = defaultProps.theme,
    validationState,
    value,
  } = props;

  const initialState = getInputsState(value);
  const [dayValue, setDayValue] = useState(initialState.dayValue);
  const [monthValue, setMonthValue] = useState(initialState.monthValue);
  const [yearValue, setYearValue] = useState(initialState.yearValue);

  const inputRefsRecord = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    const state = getInputsState(value);
    setDayValue(state.dayValue);
    setMonthValue(state.monthValue);
    setYearValue(state.yearValue);
  }, [value]);

  const getDateFromValues = useCallback((day: string, month: string, year: string) => {
    const allFields =
      day.length === DAY_LENGTH && month.length === MONTH_LENGTH && year.length === YEAR_LENGTH;

    if (allFields) {
      const date = moment([parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)]);
      return date.isValid() ? date.valueOf() : false;
    }

    return null;
  }, []);

  const handleChange = useCallback(
    (
      inputRef: string,
      inputValue: string,
      maxLength: number,
      nextRef: string | null | undefined,
    ) => {
      if (!isReadOnly && !isLoading) {
        let newDay = dayValue;
        let newMonth = monthValue;
        let newYear = yearValue;

        if (inputRef === 'dayValue') {
          newDay = inputValue;
        } else if (inputRef === 'monthValue') {
          newMonth = inputValue;
        } else if (inputRef === 'yearValue') {
          newYear = inputValue;
        }

        if (inputRef === 'dayValue') {
          setDayValue(inputValue);
        } else if (inputRef === 'monthValue') {
          setMonthValue(inputValue);
        } else if (inputRef === 'yearValue') {
          setYearValue(inputValue);
        }

        const dateResult = getDateFromValues(newDay, newMonth, newYear);
        onChange(name || '', dateResult);

        if (nextRef && inputValue.length === maxLength) {
          inputRefsRecord.current[nextRef]?.focus();
        }
      }
    },
    [dayValue, monthValue, yearValue, isReadOnly, isLoading, onChange, name, getDateFromValues],
  );

  const handleClear = useCallback(
    (e: React.SyntheticEvent) => {
      setDayValue('');
      setMonthValue('');
      setYearValue('');
      onClearProp(e);
    },
    [onClearProp],
  );

  const getInputOrder = useCallback(
    (type: string) => {
      return dateFormat.toLowerCase().indexOf(type);
    },
    [dateFormat],
  );

  const inputs = sortBy(
    [
      {
        ref: 'dayValue',
        placeholder: props.placeholderDay || '',
        value: dayValue,
        order: getInputOrder(DAY),
        maxLength: DAY_LENGTH,
      },
      {
        ref: 'monthValue',
        placeholder: props.placeholderMonth || '',
        value: monthValue,
        order: getInputOrder(MONTH),
        maxLength: MONTH_LENGTH,
      },
      {
        ref: 'yearValue',
        placeholder: props.placeholderYear || '',
        value: yearValue,
        order: getInputOrder(YEAR),
        maxLength: YEAR_LENGTH,
      },
    ],
    ['order'],
  );

  const reactId = useId();
  const labelStr = typeof label === 'string' ? cleanupId(label) : '';
  const id = idProp ?? (labelStr ? `InputDate_${labelStr}` : `InputDate_${reactId}`);

  const hasValue = Boolean(dayValue) || Boolean(monthValue) || Boolean(yearValue);
  const hasClearEnabled = Boolean(hasClear && hasValue);
  const { thInput, thInputWrapper, thSeparator } = themes[theme](props) as DefaultThemeStyles<
    typeof themes
  >;

  return (
    <FormControl
      className={classnames('direction-ltr', className)}
      hasClear={hasClearEnabled}
      iconLeft={iconLeft}
      id={id}
      isDisabled={isDisabled}
      isFilled={isFilled && hasValue}
      isLoading={isLoading}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      label={label}
      notes={notes}
      onClear={handleClear}
      theme={theme}
      validationState={validationState}
    >
      {inputs.map((input, index) => {
        const nextRef = index + 1 < inputs.length ? inputs[index + 1].ref : null;
        return (
          <Fragment key={index}>
            {!!index && (
              <span className={thSeparator} role="presentation">
                /
              </span>
            )}
            <span className={thInputWrapper}>
              <input
                className={thInput}
                data-testid={input.ref}
                disabled={isDisabled}
                {...(index === 0
                  ? {
                      id,
                    }
                  : null)}
                maxLength={input.maxLength}
                onBlur={onBlur}
                onChange={(e) => handleChange(input.ref, e.target.value, input.maxLength, nextRef)}
                onClick={onClick}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                placeholder={input.placeholder}
                readOnly={Boolean(isReadOnly || isLoading)}
                ref={(node) => {
                  inputRefsRecord.current[input.ref] = node;
                }}
                required={isRequired}
                type="tel"
                value={input.value}
              />
            </span>
          </Fragment>
        );
      })}
    </FormControl>
  );
}
